import { Fragment, forwardRef } from 'react';
import { Outlet } from 'react-router-dom';

import { attachComponentData } from '@backstage/core-plugin-api';

import {
  PageLayoutMui,
  PropsBridgeProvider,
  usePropsBridge,
} from '@spotify/confidence-core-react';

import { PageRootBridgeProps } from '../types';
import {
  ContentTabsHeader,
  PageContentProps,
  getPageContentLayoutProps,
} from './Content';
import { PAGE_CONTENT_KEY, usePageContentChildren } from './composition';
import { resolvePageHeaderLayoutProps } from './resolvePageHeaderLayoutProps';
import { PageContentBridgeProps } from './types';
import { useActionBarMui } from './useActionBarMui';
import { useContentOffsets } from './useContentOffsets';

export const ContentMui = forwardRef<HTMLDivElement, PageContentProps>(
  (props, ref) => {
    const {
      children,
      headerTabs: headerTabsProp,
      headerTabsRef: headerTabsRefProp,
    } = props;
    const layoutProps = getPageContentLayoutProps(props);
    const bridgeProps = usePropsBridge<
      PageRootBridgeProps & { contentTopOffset?: number }
    >();

    const { wrapperElements } = bridgeProps;

    const {
      sideBarLeft,
      sideBarRight,
      actionBar,
      tabsRouteRefs,
      mainChildren,
    } = usePageContentChildren(children);

    const {
      actionBarRef,
      tabsRef,
      isNested,
      actionBarTopOffset,
      contentTopOffset,
    } = useContentOffsets(bridgeProps.contentTopOffset);

    const renderedActionBar = useActionBarMui({
      wrapperElements,
      actionBar,
      actionBarRef,
      actionBarTopOffset,
    });

    const body = (
      <PropsBridgeProvider<PageContentBridgeProps>
        {...bridgeProps}
        contentTopOffset={contentTopOffset}
      >
        {mainChildren}
        {tabsRouteRefs && <Outlet />}
      </PropsBridgeProvider>
    );

    if (isNested) {
      return (
        <Fragment>
          {renderedActionBar}
          {body}
        </Fragment>
      );
    }

    const headerTabs = tabsRouteRefs ? (
      <ContentTabsHeader routeRefs={tabsRouteRefs} />
    ) : (
      headerTabsProp
    );
    const headerTabsRef = tabsRouteRefs ? tabsRef : headerTabsRefProp;
    const headerLayoutProps = resolvePageHeaderLayoutProps({
      page: bridgeProps,
      content: props,
    });

    return (
      <PageLayoutMui
        ref={ref}
        {...layoutProps}
        {...headerLayoutProps}
        sideBarLeft={sideBarLeft}
        sideBarRight={sideBarRight}
        headerTabs={headerTabs}
        headerTabsRef={headerTabsRef}
      >
        {renderedActionBar}
        {body}
      </PageLayoutMui>
    );
  },
);
ContentMui.displayName = 'ContentMui';

attachComponentData(ContentMui, PAGE_CONTENT_KEY, true);
